import { useCallback } from 'react';
import { makeStyles, tokens, Button, Tooltip, mergeClasses } from '@fluentui/react-components';

const useStyles = makeStyles({
  button: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    padding: '0',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'opacity, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    cursor: 'pointer',
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    // Auto-reveal on keyboard focus so the button is never an invisible focus
    // target (WCAG 2.4.7 Focus Visible / 2.4.11 Focus Not Obscured).
    '&:focus-visible': {
      opacity: 1,
      transform: 'scale(1)',
      pointerEvents: 'auto',
    },
  },
  hidden: {
    opacity: 0,
    transform: 'scale(0.8)',
    // Suppress pointer interaction while visually hidden so the invisible
    // button can't be accidentally clicked before the reveal animation runs.
    pointerEvents: 'none',
  },
  visible: {
    opacity: 1,
    transform: 'scale(1)',
  },
});

export interface EdgePillButtonProps {
  /** Icon element (pre-colored by caller). */
  icon: JSX.Element;
  /** Tooltip + accessible name. */
  tooltip: string;
  ariaLabel: string;
  /** When false the button is faded out (but still focusable + auto-revealed on focus). */
  isRevealed: boolean;
  onActivate: () => void;
  /** Extra class for consumer-specific positioning (e.g. absolute placement). */
  className?: string;
}

/**
 * Shared circular hover-reveal pill used by edge components (`AddButtonEdge`,
 * `DottedEdge`, `TerminalEdge` agent affordance). Renders a 24px circular
 * Button that fades + scales between hidden / visible states. The button
 * stays in the tab order regardless of reveal state and auto-reveals on
 * `:focus-visible` so keyboard users always see where focus is.
 */
export const EdgePillButton = ({
  icon,
  tooltip,
  ariaLabel,
  isRevealed,
  onActivate,
  className,
}: EdgePillButtonProps) => {
  const styles = useStyles();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onActivate();
    },
    [onActivate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
    [onActivate],
  );

  return (
    <Tooltip content={tooltip} relationship="label">
      <Button
        className={mergeClasses(
          styles.button,
          isRevealed ? styles.visible : styles.hidden,
          className,
        )}
        icon={icon}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        appearance="subtle"
        size="small"
      />
    </Tooltip>
  );
};

export default EdgePillButton;
